/**
 * Learning Paths — the academy's authoring surface.
 *
 * ONE ROUTE, TWO VIEWS: the listing, and the editor for whichever path is named in
 * `?path=`. A query parameter rather than a nested route because the choice is then a link
 * somebody can send and a state a reload survives, which is the same reason Enrolments,
 * Progress, Grades and Certificates keep their course in the query string.
 *
 * WHAT THE SERVER DECIDES, AND THIS SCREEN ONLY DRAWS:
 *
 *   `writable_orgs` comes back with the listing, and it is the difference between "on my
 *   list" and "mine to edit". A Host may legitimately serve another academy's org so its
 *   learners can take those courses — so that academy's paths appear here, read-only, with
 *   the reason on the row. Hiding them would be a lie of a different kind: those paths are
 *   on this site and this administrator will be asked about them.
 *
 *   Every refusal is shown with the server's own sentence. The authoring API answers a bad
 *   reorder with the exact steps that are missing and a forbidden course with that course's
 *   key, and none of that survives being replaced with "something went wrong". There are no
 *   client-side guards in front of those calls: a control the server would refuse still
 *   makes the call and still reports what came back.
 *
 * THE COURSE PICKER IS THE ACADEMY'S OWN COURSE LIST — `useAcademyCourses`, the same query
 * the other four admin screens share, so it is one cache and one definition of "this
 * academy's courses". It is not a permission check: the server decides what may be added,
 * and if it disagrees, the 403 is displayed rather than pre-empted.
 *
 * A PATH IS BUILT IN ONE PASS. Create asks for the courses too, and sends them with the
 * create — `POST /paths` takes `steps` and checks every one of them against the tenant
 * BEFORE it makes anything, so a refused course leaves nothing behind. Creating an empty
 * path and then going back into the editor to fill it was not just two trips: between them
 * sat a path with no courses, which no learner can complete and which the Publish button
 * would happily have released.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Banner,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  Field,
  Icon,
  SearchField,
  SelectField,
  Spinner,
  Toolbar,
  type Column,
} from '../../shared/ui';
import { pathHref } from '../../shared/lib/paths';
import {
  addPathLearner,
  addStep,
  bulkAddPathLearners,
  canWrite,
  createPath,
  deletePath,
  derivePathKey,
  getAdminPath,
  LEVELS,
  listAdminPaths,
  listPathLearners,
  orderedSteps,
  pathwayProblem,
  publishPath,
  removePathLearner,
  removeStep,
  setStepOrder,
  unpublishPath,
  updatePath,
  type AdminPath,
} from '../../shared/lib/pathways-admin';
import {
  coursePriceView,
  formatMoney,
  minorUnitDigits,
  pathwayPriceView,
  previewPathwayTotals,
  pricingEditable,
  PRICING_SEAM_KEY,
  savePathwayPrice,
  type PathwayMode,
} from '../../shared/lib/pricing';
import {
  useAdminCoursePrices,
  useAdminPathwayPrice,
  usePricingTenant,
} from '../../shared/lib/usePricing';
import { PricingGateNotice, SummaryRow } from '../../shared/ui/price';
import { LearnerRoster } from '../enrolments/LearnerRoster';
import { FreeEnrolmentAction } from '../payments/FreeEnrolmentAction';
import { PageHeader } from '../AdminShell';
import { useAcademyCourses } from '../useAcademyCourses';
import './admin-paths.css';

/** One cache key for the whole surface: the listing carries every field the editor needs. */
const PATHS_QUERY = ['pathways-admin'];

/**
 * Everything about a path that is not its courses, its key or its publication.
 *
 * Exactly the wrapper's `WRITABLE_FIELDS`, so create and edit ask for the same things and
 * neither can drift into offering a field the API would drop on the floor.
 */
interface PathFields {
  display_name: string;
  subtitle: string;
  description: string;
  level: string;
  duration: string;
  time_commitment: string;
  sequential: boolean;
}

const EMPTY_FIELDS: PathFields = {
  display_name: '',
  subtitle: '',
  description: '',
  level: '',
  duration: '',
  time_commitment: '',
  sequential: true,
};

const fieldsOf = (path: AdminPath): PathFields => ({
  display_name: path.display_name,
  subtitle: path.subtitle,
  description: path.description,
  level: path.level,
  duration: path.duration,
  time_commitment: path.time_commitment,
  sequential: path.sequential,
});

/** The text fields, trimmed. The booleans and the select pass through. */
const trimmed = (fields: PathFields): PathFields => ({
  ...fields,
  display_name: fields.display_name.trim(),
  subtitle: fields.subtitle.trim(),
  description: fields.description.trim(),
  duration: fields.duration.trim(),
  time_commitment: fields.time_commitment.trim(),
});

const sameFields = (a: PathFields, b: PathFields): boolean =>
  (Object.keys(a) as (keyof PathFields)[]).every((name) => a[name] === b[name]);

const LEVEL_OPTIONS = LEVELS.map((entry) => [entry.value, entry.label] as [string, string]);

export default function AdminPaths() {
  const [params, setParams] = useSearchParams();
  const selected = params.get('path') ?? '';
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const paths = useQuery({ queryKey: PATHS_QUERY, queryFn: listAdminPaths, retry: false });

  const open = (key: string) => setParams(key ? { path: key } : {}, { replace: true });

  const writableOrgs = paths.data?.writable_orgs ?? [];
  const all = useMemo(() => paths.data?.results ?? [], [paths.data]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matching = needle
      ? all.filter(
          (path) =>
            path.display_name.toLowerCase().includes(needle) ||
            path.key.toLowerCase().includes(needle),
        )
      : all;
    // Newest first. An administrator arriving here has almost always just made the thing
    // they are looking for.
    return [...matching].sort((a, b) => b.created.localeCompare(a.created));
  }, [all, search]);

  if (paths.isError) {
    const problem = pathwayProblem(paths.error);
    return (
      <>
        <PageHeader title="Learning Paths" description="Multi-course paths offered by this academy." />
        <Banner tone="danger">
          <strong>{problem.headline}</strong> {problem.detail}
        </Banner>
      </>
    );
  }

  if (selected) {
    const found = all.find((path) => path.key === selected);
    return (
      <PathEditor
        pathKey={selected}
        listed={found}
        writable={found ? canWrite(found, writableOrgs) : false}
        loading={paths.isPending}
        onBack={() => open('')}
      />
    );
  }

  const columns: Column<AdminPath>[] = [
    {
      key: 'path',
      header: 'Learning path',
      render: (path) => (
        <>
          {canWrite(path, writableOrgs) ? (
            <a
              href={`?path=${encodeURIComponent(path.key)}`}
              onClick={(event) => {
                event.preventDefault();
                open(path.key);
              }}
            >
              {path.display_name}
            </a>
          ) : (
            path.display_name
          )}
          <div className="admin__sub">{path.key}</div>
        </>
      ),
    },
    {
      key: 'courses',
      header: 'Courses',
      numeric: true,
      render: (path) => path.steps.length,
    },
    {
      key: 'sequence',
      header: 'Sequence',
      render: (path) => (
        <span className="paths-admin__seq">
          <Icon name={path.sequential ? 'linear_scale' : 'apps'} />
          {path.sequential ? 'In order' : 'Any order'}
        </span>
      ),
    },
    {
      key: 'state',
      header: 'State',
      render: (path) => (
        <div className="admin__badges">
          {path.published ? (
            <Badge tone="success">Published</Badge>
          ) : (
            <Badge tone="warning">Unpublished</Badge>
          )}
          {!canWrite(path, writableOrgs) && <Badge>Read only</Badge>}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (path) => (
        <div className="admin__row-actions">
          {canWrite(path, writableOrgs) ? (
            <button type="button" className="ui-btn" data-variant="ghost" onClick={() => open(path.key)}>
              Edit
            </button>
          ) : (
            <Link className="ui-btn" data-variant="ghost" to={pathHref(path.key)}>
              View
            </Link>
          )}
        </div>
      ),
    },
  ];

  const readOnlyCount = all.filter((path) => !canWrite(path, writableOrgs)).length;

  return (
    <>
      <PageHeader
        title="Learning Paths"
        description="Multi-course paths offered by this academy."
        actions={
          writableOrgs.length > 0 && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Icon name="add" />
              New learning path
            </Button>
          )
        }
      />

      {/* Said once, plainly, rather than letting the New button 404. Authoring wants an
          org-wide role on an org this site serves, which a staff account may not hold. */}
      {!paths.isPending && writableOrgs.length === 0 && (
        <Banner tone="info">
          <strong>You can see this academy's learning paths, but not change them.</strong>{' '}
          Creating and editing a path needs an administrator role for this academy. An
          administrator can grant that from the Team page.
        </Banner>
      )}

      {creating && writableOrgs.length > 0 && (
        <NewPathCard
          orgs={writableOrgs}
          onCancel={() => setCreating(false)}
          onCreated={(path) => {
            setCreating(false);
            open(path.key);
          }}
        />
      )}

      <Toolbar>
        <div className="toolbar__end">
          <SearchField value={search} onChange={setSearch} placeholder="Search learning paths…" />
        </div>
      </Toolbar>

      {paths.isPending ? (
        <Spinner label="Loading learning paths…" />
      ) : all.length === 0 ? (
        <EmptyState
          title="No learning paths yet"
          body="A learning path bundles several of this academy's courses into one sequence. Create one to get started."
          action={
            writableOrgs.length > 0 && (
              <Button variant="primary" onClick={() => setCreating(true)}>
                <Icon name="add" />
                New learning path
              </Button>
            )
          }
        />
      ) : (
        <Card>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(path) => path.key}
            empty={`Nothing matches “${search}”.`}
          />
          {readOnlyCount > 0 && (
            <p className="admin-note">
              {readOnlyCount} of these {readOnlyCount === 1 ? 'path belongs' : 'paths belong'} to
              another academy whose courses this site carries. Learners here can take them; only
              the academy that owns them can change them.
            </p>
          )}
        </Card>
      )}
    </>
  );
}

/* ============================================================== shared fields ===== */

/**
 * The seven writable fields, asked for identically wherever a path is written.
 *
 * ONE COMPONENT FOR CREATE AND EDIT, because the alternative — two forms that happen to
 * agree — is how `subtitle`, `level`, `duration` and `time_commitment` came to be accepted
 * by the API, present on the model, rendered on the learner's page and askable in neither
 * form. A field added here appears in both places or in neither.
 *
 * `level` is a select and not a text box because upstream declares it with `choices` and the
 * wrapper calls `full_clean()`: 'Beginner' typed by hand is a 400, 'beginner' is a save.
 * `duration` and `time_commitment` genuinely ARE free text on the model — "6 Weeks",
 * "4-6 hours/week" — so they stay text, with the model's own examples as the hint.
 */
function PathFieldset({
  idPrefix,
  value,
  onChange,
  disabled,
}: {
  idPrefix: string;
  value: PathFields;
  onChange: (patch: Partial<PathFields>) => void;
  disabled?: boolean;
}) {
  return (
    <>
      <Field
        label="Title"
        name={`${idPrefix}-title`}
        value={value.display_name}
        onChange={(event) => onChange({ display_name: event.target.value })}
        hint="What learners see on the learning paths page."
        maxLength={255}
        disabled={disabled}
        required
      />

      <Field
        label="Subtitle"
        name={`${idPrefix}-subtitle`}
        value={value.subtitle}
        onChange={(event) => onChange({ subtitle: event.target.value })}
        hint="One line under the title. Optional."
        disabled={disabled}
      />

      <label className="admin-field">
        <span>Description</span>
        <textarea
          name={`${idPrefix}-description`}
          rows={4}
          value={value.description}
          disabled={disabled}
          onChange={(event) => onChange({ description: event.target.value })}
        />
        <small>Who the path is for and what it covers. Shown on the path's own page.</small>
      </label>

      {/* The three at-a-glance facts a learner reads before committing, so they are grouped
          and sit on one line where there is room for it. */}
      <div className="paths-admin__facts">
        <SelectField
          label="Level"
          name={`${idPrefix}-level`}
          value={value.level}
          onChange={(event) => onChange({ level: event.target.value })}
          options={LEVEL_OPTIONS}
          disabled={disabled}
        />
        <Field
          label="Duration"
          name={`${idPrefix}-duration`}
          value={value.duration}
          onChange={(event) => onChange({ duration: event.target.value })}
          hint="How long the whole path takes. Example: 10 Weeks"
          maxLength={255}
          disabled={disabled}
        />
        <Field
          label="Time commitment"
          name={`${idPrefix}-time-commitment`}
          value={value.time_commitment}
          onChange={(event) => onChange({ time_commitment: event.target.value })}
          hint="Expected effort. Example: 4-6 hours/week"
          maxLength={255}
          disabled={disabled}
        />
      </div>

      <div className="admin-checks">
        <label>
          <input
            type="checkbox"
            name={`${idPrefix}-sequential`}
            checked={value.sequential}
            disabled={disabled}
            onChange={(event) => onChange({ sequential: event.target.checked })}
          />
          {/* NOT "must be taken in order", which is what this said and what nothing in the
              platform makes true. The flag changes how the path is PRESENTED — numbered and
              sequenced, with a recommended next course — and there is no server-side gate
              behind it. An author who believes the order is enforced designs a curriculum
              around a lock that is not there. */}
          <span>
            Show the courses as a recommended sequence
            <small>
              Learners see the courses numbered and in this order, and are pointed at the next
              one. Nothing prevents them opening a later course first. Turn this off for a path
              whose courses have no natural order.
            </small>
          </span>
        </label>
      </div>
    </>
  );
}

/* =============================================================== shared steps ===== */

/**
 * The courses of a path, numbered, with the controls that change their order.
 *
 * Shared by the create form and the editor so that a path being built and a path being
 * edited look and behave the same — the numbers mean the same thing, the arrows sit in the
 * same place. What differs is only where the change goes: the editor sends each one to the
 * server, the create form holds the list until the path exists.
 *
 * `onMove` and `onRemove` absent means read-only, which is the case for another academy's
 * path.
 */
function StepRows({
  courseKeys,
  nameFor,
  unknown,
  busy,
  onMove,
  onRemove,
}: {
  courseKeys: string[];
  nameFor: (courseKey: string) => string;
  /** True for a course this academy's list does not contain — said, not hidden. */
  unknown?: (courseKey: string) => boolean;
  busy?: boolean;
  onMove?: (index: number, delta: number) => void;
  onRemove?: (courseKey: string) => void;
}) {
  return (
    <ol className="paths-admin__steps">
      {courseKeys.map((courseKey, index) => (
        <li key={courseKey} className="paths-admin__step">
          <span className="paths-admin__num" aria-hidden="true">
            {index + 1}
          </span>

          <div className="paths-admin__step-body">
            <strong>{nameFor(courseKey)}</strong>
            <span className="admin__sub">{courseKey}</span>
            {/* A step whose course is not in this academy's list keeps its row and says
                so. Dropping it would make the path claim fewer courses than it has. */}
            {unknown?.(courseKey) && (
              <span className="admin-sub">
                Not in this academy's course list — it may have been removed.
              </span>
            )}
          </div>

          {(onMove || onRemove) && (
            <div className="admin__row-actions">
              {onMove && (
                <>
                  <button
                    type="button"
                    className="ui-btn"
                    data-variant="ghost"
                    aria-label={`Move ${nameFor(courseKey)} earlier`}
                    disabled={index === 0 || busy}
                    onClick={() => onMove(index, -1)}
                  >
                    <Icon name="arrow_upward" />
                  </button>
                  <button
                    type="button"
                    className="ui-btn"
                    data-variant="ghost"
                    aria-label={`Move ${nameFor(courseKey)} later`}
                    disabled={index === courseKeys.length - 1 || busy}
                    onClick={() => onMove(index, 1)}
                  >
                    <Icon name="arrow_downward" />
                  </button>
                </>
              )}
              {onRemove && (
                <button
                  type="button"
                  className="ui-btn"
                  data-variant="ghost"
                  aria-label={`Remove ${nameFor(courseKey)}`}
                  disabled={busy}
                  onClick={() => onRemove(courseKey)}
                >
                  Remove
                </button>
              )}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

/**
 * Choose one of this academy's courses.
 *
 * The list is `useAcademyCourses().all` — the academy's own catalogue, and the only source
 * of courses this screen has. It is NOT a permission check standing in front of the server:
 * the tenant gate is the server's, a course it refuses comes back as a 403 naming that
 * course, and the caller shows that sentence. Offering the tenant's own list is simply the
 * difference between a picker and a text box for a course key.
 *
 * `exclude` drops what is already in the path, because the server answers a duplicate with a
 * 409 and an option whose only outcome is an error is not a choice.
 */
function CoursePicker({
  courses,
  exclude,
  value,
  onChange,
  onAdd,
  busy,
  adding,
}: {
  courses: ReturnType<typeof useAcademyCourses>;
  exclude: Set<string>;
  value: string;
  onChange: (courseKey: string) => void;
  onAdd: (courseKey: string) => void;
  /** Any write to this path is in flight — the whole picker waits for it. */
  busy?: boolean;
  /** This picker's own add is in flight, which is the only one worth a spinner. */
  adding?: boolean;
}) {
  if (courses.isError) {
    return (
      <Banner tone="warning">
        <strong>This academy's course list could not be loaded.</strong> Without it there is
        nothing to choose from — reload the page to try again.
      </Banner>
    );
  }

  const available = courses.all.filter((course) => !exclude.has(course.course_key));

  return (
    <div className="paths-admin__add">
      <SelectField
        label="Add a course"
        name="path-add-course"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={courses.isPending || available.length === 0}
        options={[
          [
            '',
            courses.isPending
              ? 'Loading the course list…'
              : available.length === 0
                ? courses.all.length === 0
                  ? 'This academy has no courses yet'
                  : 'Every course in this academy is already in this path'
                : 'Choose a course…',
          ],
          ...available.map(
            (course) =>
              [course.course_key, `${course.display_name} · ${course.course_key}`] as [
                string,
                string,
              ],
          ),
        ]}
      />
      <Button
        variant="secondary"
        busy={adding}
        disabled={!value || busy}
        onClick={() => onAdd(value)}
      >
        <Icon name="add" />
        Add to path
      </Button>
    </div>
  );
}

/* ================================================================== create ===== */

/**
 * New path, complete — details, courses and order, in one request.
 *
 * The key is DERIVED and shown, not asked for, with a way to change it — the same treatment
 * the Content Library page gives a library id, and for the same reason: the org inside the
 * key is what files the path under this academy, so it has to be visible, and nobody should
 * have to compose `path-v1:ORG+NUMBER+RUN+GROUP` by hand to make a path.
 *
 * THE COURSES ARE HELD LOCALLY UNTIL SUBMIT, and go with the create as `steps`. That is one
 * request, and the server checks every course against the tenant before it creates anything,
 * so a refusal leaves nothing behind to clean up. Adding them one at a time afterwards would
 * mean a real, empty, publishable path existing in between.
 *
 * The path is still created UNPUBLISHED — the server's default and upstream's. Publishing is
 * a separate, deliberate act in the editor, which is where the admin can see what they are
 * about to release.
 */
function NewPathCard({
  orgs,
  onCancel,
  onCreated,
}: {
  orgs: string[];
  onCancel: () => void;
  onCreated: (path: AdminPath) => void;
}) {
  const queryClient = useQueryClient();
  const courses = useAcademyCourses();
  const [fields, setFields] = useState<PathFields>(EMPTY_FIELDS);
  const [steps, setSteps] = useState<string[]>([]);
  const [chosen, setChosen] = useState('');
  const [org, setOrg] = useState(orgs[0] ?? '');
  const [advanced, setAdvanced] = useState(false);
  const [key, setKey] = useState('');

  const run = String(new Date().getFullYear());
  const derived = derivePathKey(org, fields.display_name, run);
  const effectiveKey = key || derived;

  const nameFor = useMemo(() => {
    const map = new Map(courses.all.map((course) => [course.course_key, course.display_name]));
    return (courseKey: string) => map.get(courseKey) ?? courseKey;
  }, [courses.all]);

  const create = useMutation({
    mutationFn: () =>
      createPath({
        key: effectiveKey,
        ...trimmed(fields),
        // Omitted rather than sent empty when nothing was chosen: an empty list is the same
        // request the API has always had, and there is no reason to make it look different.
        ...(steps.length > 0 ? { steps } : {}),
      }),
    onSuccess: (path) => {
      queryClient.invalidateQueries({ queryKey: PATHS_QUERY });
      onCreated(path);
    },
  });

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    const from = next[index];
    const to = next[target];
    if (from === undefined || to === undefined) return;
    next[index] = to;
    next[target] = from;
    setSteps(next);
  };

  const title = fields.display_name.trim();

  return (
    <Card title="New learning path">
      <form
        className="console__form"
        onSubmit={(event) => {
          event.preventDefault();
          if (title) create.mutate();
        }}
      >
        <PathFieldset
          idPrefix="path"
          value={fields}
          onChange={(patch) => setFields((current) => ({ ...current, ...patch }))}
        />

        {orgs.length > 1 && (
          <SelectField
            label="Academy"
            name="path-org"
            value={org}
            onChange={(event) => setOrg(event.target.value)}
            options={orgs.map((entry) => [entry, entry])}
          />
        )}

        {/* THE COURSES, HERE, because a path with none of them cannot be completed by
            anyone and used to be the only thing this form could produce. */}
        <fieldset className="paths-admin__courses">
          <legend>
            Courses <span className="admin-count">{steps.length}</span>
          </legend>

          {steps.length === 0 ? (
            <p className="admin-note admin-muted">
              Nothing added yet. A path can be created without courses and filled in later, but
              until it has some there is nothing for a learner to complete.
            </p>
          ) : (
            <StepRows
              courseKeys={steps}
              nameFor={nameFor}
              onMove={move}
              onRemove={(courseKey) =>
                setSteps((current) => current.filter((entry) => entry !== courseKey))
              }
            />
          )}

          <CoursePicker
            courses={courses}
            exclude={new Set(steps)}
            value={chosen}
            onChange={setChosen}
            onAdd={(courseKey) => {
              setSteps((current) => [...current, courseKey]);
              setChosen('');
            }}
          />
        </fieldset>

        <p className="admin__derived">
          Path ID <code>{effectiveKey}</code>
          <button
            type="button"
            className="ui-btn"
            data-variant="ghost"
            onClick={() => setAdvanced(!advanced)}
          >
            {advanced ? 'Hide' : 'Change'}
          </button>
        </p>

        {advanced && (
          <div className="admin__advanced">
            <Field
              label="Path ID"
              name="path-key"
              value={effectiveKey}
              onChange={(event) => setKey(event.target.value)}
              hint="Permanent. Format: path-v1:ACADEMY+NAME+RUN+GROUP"
            />
          </div>
        )}

        {/* The whole create, including every course, is one refusal or none. The server's
            sentence names the course it would not take. */}
        <Problem error={create.error} when={create.isError} />

        <div className="console__form-actions">
          <Button type="submit" variant="primary" busy={create.isPending} disabled={!title}>
            Create learning path
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

/* ================================================================== editor ===== */

function PathEditor({
  pathKey,
  listed,
  writable,
  loading,
  onBack,
}: {
  pathKey: string;
  listed: AdminPath | undefined;
  writable: boolean;
  loading: boolean;
  onBack: () => void;
}) {
  /*
   * The listing is the source, and this is the fallback for the one case it cannot answer:
   * a path reached by a pasted link. The request is made rather than assumed to fail, so
   * the reason on screen is the server's — 404 for another academy's path, 503 if the
   * plugin is missing — instead of "not in the list I happen to hold".
   */
  const direct = useQuery({
    queryKey: ['pathways-admin', pathKey],
    queryFn: () => getAdminPath(pathKey),
    enabled: !loading && listed === undefined,
    retry: false,
  });

  const path = listed ?? direct.data;

  if (loading || (listed === undefined && direct.isPending && direct.fetchStatus !== 'idle')) {
    return (
      <>
        <EditorHeader title="Learning path" onBack={onBack} />
        <Spinner label="Loading this learning path…" />
      </>
    );
  }

  if (!path) {
    const problem = pathwayProblem(direct.error);
    return (
      <>
        <EditorHeader title="Learning path" onBack={onBack} />
        <Banner tone="danger">
          <strong>{problem.headline}</strong> {problem.detail}
        </Banner>
      </>
    );
  }

  return (
    <>
      <EditorHeader title={path.display_name} onBack={onBack} subtitle={path.key} />

      {!writable && (
        <Banner tone="info">
          <strong>This learning path belongs to another academy.</strong> This site carries its
          courses, so its learners can take the path — but only the academy that owns it can
          change it.
        </Banner>
      )}

      {/* KEYED ON THE PATH. The Details form seeds its fields from the record once, so
          without this a move from one path's editor to another's — a pasted link, a
          browser Back — would leave the previous path's title sitting in the form and one
          Save away from being written to the wrong path. */}
      <div className="admin-stack" key={path.key}>
        <PublicationPanel path={path} writable={writable} />
        <PricingPanel path={path} writable={writable} />
        <StepsPanel path={path} writable={writable} />
        <PathLearnersPanel path={path} writable={writable} />
        <DetailsPanel path={path} writable={writable} />
        {writable && <DangerPanel path={path} onDeleted={onBack} />}
      </div>
    </>
  );
}

function EditorHeader({
  title,
  subtitle,
  onBack,
}: {
  title: string;
  subtitle?: string;
  onBack: () => void;
}) {
  return (
    <PageHeader
      title={title}
      description={subtitle}
      actions={
        <Button variant="ghost" onClick={onBack}>
          <Icon name="arrow_back" />
          All learning paths
        </Button>
      }
    />
  );
}

/* ---------------------------------------------------------------- publication -- */

function PublicationPanel({ path, writable }: { path: AdminPath; writable: boolean }) {
  const queryClient = useQueryClient();

  const change = useMutation({
    mutationFn: () => (path.published ? unpublishPath(path.key) : publishPath(path.key)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pathways-admin'] }),
  });

  return (
    <section className="admin-panel">
      <div className="admin-panel__head">
        <h2>Publication</h2>
        {path.published ? (
          <Badge tone="success">Published</Badge>
        ) : (
          <Badge tone="warning">Unpublished</Badge>
        )}
      </div>

      <p className="admin-note">
        {path.published
          ? 'Every learner on this academy can see this path and its courses on the Learning Paths page.'
          : 'Only learners who have been added to this path can see it. It does not appear on the Learning Paths page.'}
      </p>

      <Problem error={change.error} when={change.isError} />

      {writable && (
        <div className="admin-actions">
          <Button
            variant={path.published ? 'secondary' : 'primary'}
            busy={change.isPending}
            onClick={() => change.mutate()}
          >
            <Icon name={path.published ? 'visibility_off' : 'visibility'} />
            {path.published ? 'Unpublish' : 'Publish'}
          </Button>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------- pricing -- */

/**
 * What this learning path costs, and what it saves (spec R6.7).
 *
 * IT DOES NOT EXTEND `PathFields`, AND THAT IS THE POINT. `PathFields` is exactly the
 * wrapper's `WRITABLE_FIELDS` "so create and edit ask for the same things and neither can
 * drift into offering a field the API would drop on the floor" — see its own note above. A
 * price key added there would be sent to upstream's authoring endpoint and DISCARDED IN
 * SILENCE, and the form would report a save that never happened. So this panel holds its own
 * state and writes to `PUT /api/pricing/v1/pathways/{key}`, exactly as the course Pricing card
 * stays out of the Studio draft for the same reason.
 *
 * THE SUMMARY IS COMPUTED LOCALLY AND THE SAVED NUMBERS ARE THE SERVER'S. `previewPathwayTotals`
 * moves as the author types a discount, because asking the server per keystroke is the fan-out
 * R7.1 forbids; the moment the write returns, the server's row replaces it and where the two
 * disagree the server wins (R2.5). This is the one place in the SPA that does bundle arithmetic,
 * it is never called on a learner surface, and `pricing.ts` owns every line of it.
 */
function PricingPanel({ path, writable }: { path: AdminPath; writable: boolean }) {
  const queryClient = useQueryClient();

  const tenantSeam = usePricingTenant();
  const tenant = tenantSeam?.tenant ?? null;

  // This path's saved row, admin read so an unpublished or withdrawn price is still visible.
  const savedSeam = useAdminPathwayPrice(path.key);
  const saved = pathwayPriceView(savedSeam, path.key);

  /**
   * The MEMBER COURSES' prices — the input to every number below.
   *
   * One batch keyed on this path's steps, which is what makes the summary honest: a percentage
   * has to apply to a real sum of real member prices, and the alternative is an author setting
   * "15% off" against a total nobody computed. Bounded by the path's own step count, so it
   * grows with the path and never with the catalogue.
   */
  const memberKeys = useMemo(() => path.steps.map((step) => step.course_key), [path.steps]);
  const memberSeam = useAdminCoursePrices(memberKeys);

  /**
   * Each PAID member's own base. Free members contribute nothing to the sum and are counted
   * separately, because "3 paid, 1 free" is a fact the author needs in order to read the total.
   *
   * A member whose price is `unknown` is NOT treated as free: it makes the whole total
   * unreportable, which is what `unpriceable` below carries. Counting it as zero would print a
   * confident total that is wrong by the price of a course.
   */
  const members = memberKeys.map((key) => coursePriceView(memberSeam, key));
  const paidBases = members
    .filter((view) => view.mode === 'paid' && view.baseMinor !== null)
    .map((view) => view.baseMinor as number);
  const freeCount = members.filter((view) => view.mode === 'free').length;
  const unpriceable = members.filter((view) => view.mode === 'unknown').length;

  const [mode, setMode] = useState<PathwayMode | null>(null);
  const [amount, setAmount] = useState('');
  const [discount, setDiscount] = useState('');

  const currency = tenant?.currency ?? saved.currency ?? '';
  const digits = currency ? minorUnitDigits(currency) : 2;

  // Seeded from the saved row once it lands, and never again — the same rule the course
  // Pricing card keeps, so a background refetch cannot overwrite what the author is typing.
  useEffect(() => {
    if (mode !== null || saved.wireMode === null) return;
    setMode(saved.wireMode);
    if (saved.baseMinor !== null) setAmount((saved.baseMinor / 10 ** digits).toFixed(digits));
    if (saved.discountBasisPoints !== null) setDiscount(String(saved.discountBasisPoints / 100));
  }, [saved.wireMode, saved.baseMinor, saved.discountBasisPoints, digits, mode]);

  const chosen: PathwayMode = mode ?? saved.wireMode ?? 'free';

  const amountMinor =
    amount.trim() === '' || !Number.isFinite(Number(amount))
      ? null
      : Math.round(Number(amount) * 10 ** digits);
  // Percent in the box, basis points on the wire — 15 becomes 1500. One conversion, here.
  const discountBps =
    discount.trim() === '' || !Number.isFinite(Number(discount))
      ? null
      : Math.round(Number(discount) * 100);

  const editable = writable && pricingEditable(tenant);

  let error: string | null = null;
  if (chosen === 'fixed' && (amountMinor === null || amountMinor <= 0)) {
    error = 'Enter the bundle price, as a number greater than zero.';
  } else if (chosen === 'fixed' && amountMinor !== null && amountMinor < (tenant?.minimumChargeMinor ?? 0)) {
    error = `This academy's payment account cannot charge less than ${formatMoney(tenant?.minimumChargeMinor ?? 0, currency)}.`;
  } else if (chosen === 'percent_discount' && (discountBps === null || discountBps <= 0 || discountBps > 10_000)) {
    error = 'Enter the discount as a percentage between 0 and 100.';
  }

  /**
   * The live summary, or null when no honest one can be drawn.
   *
   * `unpriceable > 0` is the case R3.3 cares about: a member course whose price could not be
   * read makes every figure below a guess, so NO TOTAL IS SHOWN rather than a wrong one. That
   * is the same rule the learner surfaces keep — "Price unavailable" is always better than a
   * confident wrong number.
   */
  const totals =
    tenant && unpriceable === 0
      ? previewPathwayTotals({
          mode: chosen,
          memberBaseMinor: paidBases,
          amountMinor,
          discountBasisPoints: discountBps,
          fee: tenant,
        })
      : null;

  const save = useMutation({
    mutationFn: () =>
      savePathwayPrice(path.key, {
        mode: chosen,
        amount_minor: chosen === 'fixed' ? amountMinor : null,
        discount_basis_points: chosen === 'percent_discount' ? discountBps : null,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PRICING_SEAM_KEY }),
  });

  return (
    <section className="admin-panel">
      <div className="admin-panel__head">
        <h2>Pricing</h2>
      </div>

      {/* Controls stay present and disabled with the reason and who to ask (R4.2, R4.4). */}
      <PricingGateNotice tenant={tenant} />

      <SelectField
        label="How this path is priced"
        name="pathway_price_mode"
        value={chosen}
        disabled={!editable}
        options={[
          ['free', 'Free'],
          ['sum', 'Sum of course prices'],
          ['fixed', 'Fixed bundle price'],
          ['percent_discount', 'Percent off the sum'],
        ]}
        onChange={(event) => setMode(event.target.value as PathwayMode)}
      />

      {chosen === 'fixed' && (
        <Field
          label={currency ? `Bundle price (${currency})` : 'Bundle price'}
          name="pathway_price_amount"
          type="number"
          inputMode="decimal"
          min="0"
          step={(1 / 10 ** digits).toFixed(digits)}
          value={amount}
          disabled={!editable}
          onChange={(event) => setAmount(event.target.value)}
          error={error ?? undefined}
          hint="What this academy keeps. Payment processing is added on top."
        />
      )}

      {chosen === 'percent_discount' && (
        <Field
          label="Discount (%)"
          name="pathway_price_discount"
          type="number"
          inputMode="decimal"
          min="0"
          max="100"
          step="0.01"
          value={discount}
          disabled={!editable}
          onChange={(event) => setDiscount(event.target.value)}
          error={error ?? undefined}
          hint="Taken off the sum of the course prices, before payment processing is added."
        />
      )}

      {/*
        THE AUTHORING SUMMARY (R6.7). Eight rows, not five, and the two that make it eight are
        the point: an author setting a percentage has to see BOTH the number the percentage
        applies to — the sum of the course prices — and the number the learner is actually
        charged. Collapsing them is how an author ends up surprised by their own price.

        "Bundle price (your net)" is deliberate: it is what the academy keeps before Stripe's
        own charge, and the processing row is money the learner pays to cover that charge. It
        is not a Mereka commission — there is none (D5).
      */}
      {totals ? (
        <dl className="pricing-summary">
          <SummaryRow
            label="Courses in this path"
            value={`${path.steps.length} (${paidBases.length} paid, ${freeCount} free)`}
          />
          <SummaryRow
            label="Sum of course prices"
            value={formatMoney(totals.separateBaseMinor, currency)}
            hint="What a percentage discount applies to"
          />
          {chosen === 'percent_discount' && (
            <SummaryRow label="Bundle discount" value={`${(discountBps ?? 0) / 100}%`} />
          )}
          <SummaryRow
            label="Bundle price (your net)"
            value={formatMoney(totals.bundleMinor, currency)}
          />
          <SummaryRow
            label="Payment processing added"
            value={formatMoney(totals.feeMinor, currency)}
          />
          <SummaryRow
            label="Learners are charged"
            value={formatMoney(totals.totalMinor, currency)}
            emphasis
          />
          <SummaryRow
            label="If bought separately, learners pay"
            value={formatMoney(totals.separateMinor, currency)}
          />
          <SummaryRow label="Learners save" value={formatMoney(totals.savingMinor, currency)} />
        </dl>
      ) : (
        <Banner tone="warning">
          {unpriceable > 0
            ? 'The courses in this path could not be read, so no total can be shown. This is a platform problem, not a setting — report it before publishing a price.'
            : 'This academy’s payment settings could not be read, so no total can be shown.'}
        </Banner>
      )}

      {/* Stated under the summary, because a free member is counted in the total as zero and an
          author reading a sum needs to know one of its terms was free rather than missing. */}
      {totals && freeCount > 0 && (
        <p className="admin-note">
          {freeCount === 1
            ? '1 course in this path has no price. It is counted as free in the total.'
            : `${freeCount} courses in this path have no price. They are counted as free in the total.`}
        </p>
      )}

      {saved.mode === 'unknown' && saved.reason && <Banner tone="warning">{saved.reason}</Banner>}

      <Problem error={save.error} when={save.isError} />
      {save.isSuccess && !save.isPending && (
        <Banner tone="success">Price saved. Every page showing this path now agrees.</Banner>
      )}

      {writable && (
        <div className="admin-actions">
          <Button
            variant="primary"
            busy={save.isPending}
            // Disabled where no honest total could be drawn: publishing a price whose total
            // is unknown is the one thing R6.7's publication guard forbids.
            disabled={!editable || error !== null || (chosen !== 'free' && totals === null)}
            onClick={() => save.mutate()}
          >
            <Icon name="sell" />
            Save price
          </Button>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ learners -- */

/**
 * Who is on this pathway, and who has been invited but has not registered.
 *
 * The panel itself is shared with the course surface (`admin/enrolments/LearnerRoster`);
 * what belongs HERE is the one thing that is true of pathways and not of courses.
 *
 * NO EMAIL IS EVER SENT BY A PATHWAY INVITATION, and that is stated on screen rather than
 * left for an administrator to discover when nobody turns up. This was measured, not
 * assumed: `learning-paths-plugin` contains no mail path at all — a grep for
 * `send_mail|EmailMessage|email_students|send_email|notify` across the installed package
 * returns nothing. So `LearningPathEnrollmentAllowed` records a silent invitation and the
 * person has no way to learn about it unless the administrator tells them.
 *
 * There is deliberately no "email them" checkbox here for the same reason: a control that
 * cannot work would imply the capability exists. The course roster passes one because the
 * platform really does send that mail.
 */
function PathLearnersPanel({ path, writable }: { path: AdminPath; writable: boolean }) {
  const queryClient = useQueryClient();

  return (
    <LearnerRoster
      subject="learning path"
      writable={writable}
      api={{
        queryKey: ['pathway-learners', path.key],
        list: () => listPathLearners(path.key),
        add: (who) => addPathLearner(path.key, who),
        addMany: (emails) => bulkAddPathLearners(path.key, emails),
        remove: (identifier) => removePathLearner(path.key, identifier),
      }}
      /**
       * THE SECOND WAY OFF A PATHWAY, AND IT IS NOT THE SAME AS `Remove`.
       *
       * `Remove` calls the pathways API: it withdraws this pathway enrolment and stops there.
       * The member courses the pathway gave the learner are left behind, nothing is written to
       * the payment audit trail, and no reason is recorded. That is the right operation for a
       * mistaken invite and the wrong one for withdrawing an enrolment somebody was given.
       *
       * This one goes through payment operations: it previews every course the enrolment
       * touches, KEEPS anything the learner owns independently of it — bought separately,
       * granted by another pathway, held before — makes each unproven revocation something the
       * operator has to confirm by name, requires a reason, and writes a permanent audit row.
       *
       * OFFERED ONLY ON AN ENROLLED ROW. A pending invite is an address with no account and no
       * enrolment; the preview would answer `not_enrolled`, correctly, and offering the control
       * would be inviting that refusal.
       */
      rowAction={(row) =>
        row.status === 'enrolled' ? (
          <FreeEnrolmentAction
            pathwayKey={path.key}
            // `null` is "this address has no account", which is ABSENT and not an empty
            // username: the dialog treats '' as given-but-blank and would send it.
            username={row.username ?? undefined}
            email={row.email}
            label="Cancel enrolment (audited)"
            onDone={() =>
              void queryClient.invalidateQueries({ queryKey: ['pathway-learners', path.key] })
            }
          />
        ) : null
      }
      inviteNote={
        <Banner tone="warning">
          <strong>Nobody is emailed when you add them to a learning path.</strong> An address
          with no account yet becomes a <em>pending invite</em>: it turns into a real
          enrolment the moment that person registers with that exact address — but the
          platform sends them nothing, so you have to tell them yourself. Somebody who
          already has an account is enrolled straight away and will see the path next time
          they sign in.
        </Banner>
      }
    />
  );
}

/* --------------------------------------------------------------------- steps -- */

/**
 * The courses, in order.
 *
 * ORDER IS THE WHOLE POINT of a learning path, so it is the first thing on the row and the
 * control for it is a pair of arrows rather than drag-and-drop. Arrows are reachable by
 * keyboard, announce themselves to a screen reader, and cannot half-happen on a touch
 * screen; a reorder that silently drops a course is a worse outcome than a slightly less
 * fashionable control.
 *
 * Each move sends the WHOLE new order, which is what the endpoint takes — it rejects a
 * partial list rather than reinterpreting it, so two steps can never end up sharing a
 * position.
 */
function StepsPanel({ path, writable }: { path: AdminPath; writable: boolean }) {
  const queryClient = useQueryClient();
  const courses = useAcademyCourses();
  const [chosen, setChosen] = useState('');

  const steps = orderedSteps(path);

  const byKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const course of courses.all) map.set(course.course_key, course.display_name);
    return map;
  }, [courses.all]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['pathways-admin'] });

  const reorder = useMutation({
    mutationFn: (order: string[]) => setStepOrder(path.key, order),
    onSuccess: invalidate,
  });

  const add = useMutation({
    mutationFn: (courseKey: string) => addStep(path.key, courseKey),
    onSuccess: () => {
      setChosen('');
      invalidate();
    },
  });

  const drop = useMutation({
    mutationFn: (courseKey: string) => removeStep(path.key, courseKey),
    onSuccess: invalidate,
  });

  const move = (index: number, delta: number) => {
    const order = steps.map((step) => step.course_key);
    const target = index + delta;
    const from = order[index];
    const to = order[target];
    if (from === undefined || to === undefined) return;
    order[index] = to;
    order[target] = from;
    reorder.mutate(order);
  };

  const inPath = new Set(steps.map((step) => step.course_key));

  const busy = reorder.isPending || add.isPending || drop.isPending;

  return (
    <section className="admin-panel">
      <div className="admin-panel__head">
        <h2>Courses</h2>
        <span className="admin-count">{steps.length}</span>
      </div>

      <p className="admin-note">
        {path.sequential
          ? 'Learners see these in the order shown and are pointed at the next one. The order is a recommendation, not a restriction.'
          : 'Learners can take these in any order. The order below is how they are listed.'}
      </p>

      {steps.length === 0 ? (
        <p className="admin-note admin-muted">
          No courses yet. A path with no courses cannot be completed by anyone, so add at least
          one before publishing it.
        </p>
      ) : (
        <StepRows
          courseKeys={steps.map((step) => step.course_key)}
          nameFor={(courseKey) => byKey.get(courseKey) ?? courseKey}
          unknown={(courseKey) => !byKey.has(courseKey) && !courses.isPending}
          busy={busy}
          onMove={writable ? move : undefined}
          onRemove={writable ? (courseKey) => drop.mutate(courseKey) : undefined}
        />
      )}

      <Problem error={reorder.error} when={reorder.isError} />
      <Problem error={drop.error} when={drop.isError} />
      <Problem error={add.error} when={add.isError} />

      {writable && (
        <CoursePicker
          courses={courses}
          exclude={inPath}
          value={chosen}
          onChange={setChosen}
          onAdd={(courseKey) => add.mutate(courseKey)}
          busy={busy}
          adding={add.isPending}
        />
      )}
    </section>
  );
}

/* ------------------------------------------------------------------- details -- */

/**
 * Everything about the path that is not its courses or its publication.
 *
 * ALL SEVEN WRITABLE FIELDS, not the three this form used to ask for. `subtitle`, `level`,
 * `duration` and `time_commitment` are in the wrapper's `WRITABLE_FIELDS`, are on upstream's
 * model, and are read by the learner's own page — they were simply never asked for, so the
 * only way to set them was the Django admin.
 *
 * The PATCH sends the whole set rather than a diff. The endpoint refuses a body with no
 * writable field in it ("No writable fields supplied"), and sending all seven is what makes
 * clearing one — a subtitle deleted down to nothing — a real change rather than a no-op the
 * diff would drop.
 */
function DetailsPanel({ path, writable }: { path: AdminPath; writable: boolean }) {
  const queryClient = useQueryClient();
  const saved = fieldsOf(path);
  const [fields, setFields] = useState<PathFields>(saved);

  const save = useMutation({
    mutationFn: () => updatePath(path.key, trimmed(fields)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pathways-admin'] }),
  });

  const changed = !sameFields(trimmed(fields), saved);
  const title = fields.display_name.trim();

  return (
    <section className="admin-panel">
      <div className="admin-panel__head">
        <h2>Details</h2>
      </div>

      <form
        className="console__form"
        onSubmit={(event) => {
          event.preventDefault();
          if (title) save.mutate();
        }}
      >
        <PathFieldset
          idPrefix="edit-path"
          value={fields}
          onChange={(patch) => setFields((current) => ({ ...current, ...patch }))}
          disabled={!writable}
        />

        <Problem error={save.error} when={save.isError} />

        {save.isSuccess && !changed && (
          <Banner tone="success">
            <strong>Saved.</strong> Learners see the new details straight away.
          </Banner>
        )}

        {writable && (
          <div className="console__form-actions">
            <Button
              type="submit"
              variant="primary"
              busy={save.isPending}
              disabled={!changed || !title}
            >
              Save changes
            </Button>
          </div>
        )}
      </form>
    </section>
  );
}

/* -------------------------------------------------------------------- danger -- */

function DangerPanel({ path, onDeleted }: { path: AdminPath; onDeleted: () => void }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const remove = useMutation({
    mutationFn: () => deletePath(path.key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pathways-admin'] });
      setConfirming(false);
      onDeleted();
    },
  });

  return (
    <section className="admin-panel">
      <div className="admin-panel__head">
        <h2>Delete</h2>
      </div>

      <p className="admin-note">
        Deleting this path removes its course list and every learner's place in it. The courses
        themselves are not touched, and learners stay enrolled in the ones they have joined.
      </p>

      <Problem error={remove.error} when={remove.isError} />

      <div className="admin-actions">
        <Button variant="danger" onClick={() => setConfirming(true)}>
          Delete this learning path
        </Button>
      </div>

      {confirming && (
        <ConfirmDialog
          title="Delete this learning path?"
          body={
            <p>
              <strong>{path.display_name}</strong> and every learner's place in it will be
              deleted. This cannot be undone.
            </p>
          }
          confirmLabel="Delete learning path"
          busy={remove.isPending}
          onConfirm={() => remove.mutate()}
          onClose={() => setConfirming(false)}
        />
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ problems -- */

/**
 * A refusal, in the server's own words.
 *
 * The headline says what kind of problem it is; the detail is the sentence the API sent,
 * unedited. That sentence is the useful half — it names the course that is not available,
 * or the steps a reorder left out — and rewriting it into house style would delete the only
 * part worth reading.
 */
function Problem({ error, when }: { error: unknown; when: boolean }) {
  if (!when) return null;
  const problem = pathwayProblem(error);

  return (
    <Banner tone="danger">
      <strong>{problem.headline}</strong> {problem.detail}
    </Banner>
  );
}
